// Page Mon Cercle de Confiance - Pour les utilisateurs "Super Confiance"
// Permet de voir et inviter des personnes de confiance

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, ShieldCheck, Users, UserPlus, Mail, 
  Trash2, Clock, CheckCircle, X, Search, RefreshCw, Send,
  Crown, Info, Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MonCercleConfiancePage() {
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, registered: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ email: '', name: '', note: '' });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // Récupérer l'email de l'utilisateur connecté depuis le localStorage ou context
    const storedProfile = localStorage.getItem('social_profile');
    if (storedProfile) {
      try {
        const profile = JSON.parse(storedProfile);
        // On a besoin de l'email pour filtrer les invitations
        const storedUser = localStorage.getItem('social_user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          setUserEmail(user.email?.toLowerCase() || '');
        }
      } catch (e) {
        console.error('Error parsing profile:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (userEmail) {
      fetchMyInvites();
    }
  }, [userEmail]);

  const fetchMyInvites = async () => {
    if (!userEmail) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/trust-circle/invites/by/${userEmail}`);
      const data = await res.json();
      
      if (res.ok) {
        setInvites(data.invites || []);
        setStats({
          total: data.total || 0,
          pending: (data.invites || []).filter(i => i.status === 'pending').length,
          registered: (data.invites || []).filter(i => i.status === 'registered').length
        });
      }
    } catch (error) {
      console.error('Erreur chargement invitations:', error);
    }
    setLoading(false);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!formData.email || !formData.name) {
      setFormError('Email et nom requis');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/trust-circle/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          trust_level: 'confiance', // Super Confiance ne peut inviter que des "Confiance"
          note: formData.note,
          invited_by_email: userEmail // Pour traçabilité
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setFormError(data.detail || 'Erreur lors de l\'invitation');
        return;
      }

      const emailStatus = data.email_sent 
        ? `Invitation envoyée à ${formData.email} (email envoyé)` 
        : `Invitation créée pour ${formData.email}`;
      
      setFormSuccess(emailStatus);
      setFormData({ email: '', name: '', note: '' });
      fetchMyInvites();
      
      setTimeout(() => {
        setShowAddModal(false);
        setFormSuccess('');
      }, 2000);
    } catch (error) {
      setFormError('Erreur réseau');
    }
  };

  const handleDelete = async (inviteId) => {
    if (!window.confirm('Supprimer cette invitation ?')) return;

    try {
      const res = await fetch(`${API_URL}/api/trust-circle/invite/${inviteId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        fetchMyInvites();
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const handleResendEmail = async (inviteId, email) => {
    try {
      const res = await fetch(`${API_URL}/api/trust-circle/resend/${inviteId}`, {
        method: 'POST'
      });

      const data = await res.json();

      if (res.ok && data.success) {
        alert(`Email renvoyé à ${email}`);
      } else {
        alert(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      alert('Erreur réseau');
    }
  };

  const filteredInvites = invites.filter(inv => 
    inv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Crown size={24} color="white" />
          </div>
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: 28, 
              fontWeight: 800, 
              color: '#1E3A5F',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              Mon Cercle de Confiance
              <span style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                color: 'white',
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 20,
                fontWeight: 600
              }}>
                Super Confiance
              </span>
            </h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: 15 }}>
              Invite des personnes de confiance qui n'auront pas besoin de certification
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(168,85,247,0.05) 100%)',
          borderRadius: 16,
          padding: 20,
          marginBottom: 24,
          border: '1px solid rgba(139,92,246,0.2)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16
        }}
      >
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'rgba(139,92,246,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Gift size={20} color="#8b5cf6" />
        </div>
        <div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 700, color: '#1E3A5F' }}>
            Ton privilège Super Confiance
          </h3>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
            En tant que membre <strong>Super Confiance</strong>, tu peux inviter des personnes de ton réseau. 
            Elles bénéficieront d'une inscription simplifiée <strong>sans vérification d'identité</strong>. 
            Utilise ce privilège avec discernement.
          </p>
        </div>
      </motion.div>

      {/* Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: 16, 
        marginBottom: 24 
      }}>
        <StatCard 
          icon={Users} 
          value={stats.total} 
          label="Invitations" 
          color="#8b5cf6" 
        />
        <StatCard 
          icon={Clock} 
          value={stats.pending} 
          label="En attente" 
          color="#f59e0b" 
        />
        <StatCard 
          icon={CheckCircle} 
          value={stats.registered} 
          label="Inscrits" 
          color="#22c55e" 
        />
      </div>

      {/* Search & Actions */}
      <div style={{ 
        display: 'flex', 
        gap: 12, 
        marginBottom: 20,
        flexWrap: 'wrap'
      }}>
        <div style={{ 
          flex: 1, 
          minWidth: 200,
          position: 'relative' 
        }}>
          <Search 
            size={18} 
            style={{ 
              position: 'absolute', 
              left: 14, 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#94a3b8'
            }} 
          />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 44px',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              fontSize: 14,
              outline: 'none'
            }}
          />
        </div>
        
        <button
          onClick={fetchMyInvites}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 20px',
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            background: 'white',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
            color: '#64748b'
          }}
        >
          <RefreshCw size={16} />
          Actualiser
        </button>
        
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 24px',
            borderRadius: 12,
            border: 'none',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
            color: 'white',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(139,92,246,0.3)'
          }}
        >
          <UserPlus size={18} />
          Inviter
        </button>
      </div>

      {/* Liste des invitations */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
          Chargement...
        </div>
      ) : filteredInvites.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            textAlign: 'center',
            padding: 60,
            background: '#f8fafc',
            borderRadius: 16,
            border: '2px dashed #e2e8f0'
          }}
        >
          <Users size={48} style={{ color: '#cbd5e1', marginBottom: 16 }} />
          <h3 style={{ margin: '0 0 8px 0', color: '#64748b', fontWeight: 600 }}>
            Aucune invitation
          </h3>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: 14 }}>
            Commence par inviter des personnes de confiance
          </p>
        </motion.div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredInvites.map((invite) => (
            <InviteCard 
              key={invite.id}
              invite={invite}
              onDelete={() => handleDelete(invite.id)}
              onResendEmail={() => handleResendEmail(invite.id, invite.email)}
            />
          ))}
        </div>
      )}

      {/* Modal d'invitation */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: 20
            }}
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: 20,
                padding: 32,
                width: '100%',
                maxWidth: 480,
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1E3A5F' }}>
                  Inviter une personne
                </h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#94a3b8'
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Badge info */}
              <div style={{
                background: 'rgba(249,115,22,0.1)',
                borderRadius: 12,
                padding: 16,
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <Shield size={24} color="#F97316" />
                <div>
                  <div style={{ fontWeight: 600, color: '#F97316', fontSize: 14 }}>
                    Statut accordé : Confiance
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    Cette personne pourra s'inscrire sans certification
                  </div>
                </div>
              </div>

              <form onSubmit={handleInvite}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#374151', fontSize: 14 }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@exemple.fr"
                    style={{
                      width: '100%',
                      padding: 14,
                      borderRadius: 12,
                      border: '1px solid #e2e8f0',
                      fontSize: 15,
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#374151', fontSize: 14 }}>
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Prénom Nom"
                    style={{
                      width: '100%',
                      padding: 14,
                      borderRadius: 12,
                      border: '1px solid #e2e8f0',
                      fontSize: 15,
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, color: '#374151', fontSize: 14 }}>
                    Note (optionnel)
                  </label>
                  <textarea
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    placeholder="Ex: Mon ami d'enfance, collègue de travail..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: 14,
                      borderRadius: 12,
                      border: '1px solid #e2e8f0',
                      fontSize: 15,
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {formError && (
                  <div style={{
                    background: 'rgba(239,68,68,0.1)',
                    color: '#dc2626',
                    padding: 12,
                    borderRadius: 10,
                    marginBottom: 16,
                    fontSize: 14
                  }}>
                    {formError}
                  </div>
                )}

                {formSuccess && (
                  <div style={{
                    background: 'rgba(34,197,94,0.1)',
                    color: '#16a34a',
                    padding: 12,
                    borderRadius: 10,
                    marginBottom: 16,
                    fontSize: 14
                  }}>
                    {formSuccess}
                  </div>
                )}

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: 16,
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                    color: 'white',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  <UserPlus size={20} />
                  Envoyer l'invitation
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Composant StatCard
function StatCard({ icon: Icon, value, label, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'white',
        borderRadius: 16,
        padding: 20,
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: 16
      }}
    >
      <div style={{
        width: 48,
        height: 48,
        borderRadius: 12,
        background: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon size={24} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#1E3A5F' }}>
          {value}
        </div>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {label}
        </div>
      </div>
    </motion.div>
  );
}

// Composant InviteCard
function InviteCard({ invite, onDelete, onResendEmail }) {
  const isRegistered = invite.status === 'registered';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'white',
        borderRadius: 16,
        padding: 20,
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {/* Avatar */}
        <div style={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          background: 'rgba(249,115,22,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#F97316',
          fontWeight: 700,
          fontSize: 18
        }}>
          {invite.name.charAt(0).toUpperCase()}
        </div>
        
        {/* Info */}
        <div>
          <div style={{ fontWeight: 700, color: '#1E3A5F', fontSize: 16 }}>
            {invite.name}
          </div>
          <div style={{ fontSize: 14, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Mail size={14} />
            {invite.email}
          </div>
          {invite.note && (
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
              "{invite.note}"
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Badge Confiance */}
        <span style={{
          padding: '6px 12px',
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 600,
          background: 'rgba(249,115,22,0.1)',
          color: '#F97316'
        }}>
          Confiance
        </span>

        {/* Status */}
        <span style={{
          padding: '6px 12px',
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 600,
          background: isRegistered ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)',
          color: isRegistered ? '#22c55e' : '#f59e0b',
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}>
          {isRegistered ? <CheckCircle size={14} /> : <Clock size={14} />}
          {isRegistered ? 'Inscrit' : 'En attente'}
        </span>

        {/* Resend Email (seulement si en attente) */}
        {!isRegistered && (
          <button
            onClick={onResendEmail}
            title="Renvoyer l'email d'invitation"
            style={{
              padding: 8,
              borderRadius: 10,
              border: 'none',
              background: 'rgba(30,58,95,0.1)',
              color: '#1E3A5F',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Send size={18} />
          </button>
        )}

        {/* Delete (seulement si en attente) */}
        {!isRegistered && (
          <button
            onClick={onDelete}
            title="Supprimer l'invitation"
            style={{
              padding: 8,
              borderRadius: 10,
              border: 'none',
              background: 'rgba(239,68,68,0.1)',
              color: '#ef4444',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>
    </motion.div>
  );
}
